<template>
  <div class="report-form d-flex flex-column" v-if="!isSuccess">
    <div class="title mb-2">
      {{ $t('Пожаловаться на страницу') }}
    </div>
    <div class="sub-title mb-8">
      {{ $t('Жалоба будет отправлена на рассмотрение') }}
    </div>
    <v-textarea
        v-model="text"
        variant="outlined"
        :placeholder="$t('Опишите причину жалобы')"
        :error-messages="errorMessage ? $t(errorMessage) : ''"
        :hide-details="!errorMessage"
        rounded="lg"
        no-resize
        maxlength="350"
        clearable
        class="mb-3"
        @update:model-value="clearError"
    >
      <template v-slot:clear>
        <v-hover>
          <template v-slot:default="{ props }">
            <v-icon :icon="ClearIcon" v-bind="props" @click="text = ''; clearError()" v-if="text"/>
          </template>
        </v-hover>
      </template>
    </v-textarea>
    <v-btn
        variant="flat"
        color="primary"
        rounded="lg"
        class="w-100 report-submit"
        :loading="isLoading"
        :disabled="isLoading"
        @click="sendReport"
    >
      {{ $t('Отправить') }}
    </v-btn>
  </div>
  <div class="text-center position-relative mb-5" v-else>
    <v-icon :icon="SuccessIcon" size="40"/>
    <div class="title mt-2 mb-2">
      {{$t('Готово!')}}
    </div>
    <div class="d-flex justify-center text-center mb-4">
      <div class="delete-description">
        {{$t('Жалоба отправлена')}}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref} from "vue";
import {useRoute} from "vue-router";
import ClearIcon from "~/assets/icons/clear.vue";
import { useReCaptcha } from '@ismet/vue-ui-kit'
import {useUserStore} from "@/stores/userStore";
import SuccessIcon from "~/assets/icons/success.vue";

const route = useRoute()
const tapApi = useTapApi()
const userStore = useUserStore()
const reCaptchaStore = useReCaptcha()
const text = ref('')
const errorMessage = ref('')
const pendingMessage = ref('')
const isSuccess = ref(false)
const isLoading = ref(false)

const alias = computed(() => {
  const value = route.params.view || route.params.alias
  return Array.isArray(value) ? value[0] : value || ''
})

function clearError() {
  errorMessage.value = ''
}

const unsubscribeRecaptcha = reCaptchaStore.$onAction(({ name, args }) => {
  if (name === 'exception') {
    pendingMessage.value = ''
    isLoading.value = false
    return
  }

  if (name !== 'verified' || !pendingMessage.value) return

  const captchaToken = args[0]
  const message = pendingMessage.value
  pendingMessage.value = ''

  submitReport(message, captchaToken)
})

onMounted(() => {
  reCaptchaStore.showRecaptcha()
})

onUnmounted(() => {
  unsubscribeRecaptcha()
})

async function sendReport() {
  if (isLoading.value) return

  const message = text.value.trim()

  if (message.length < 15 || message.length > 350) {
    errorMessage.value = 'Количество символов должно быть от 15 до 350'
    return
  }

  if (!userStore.isAuth) {
    isLoading.value = true
    pendingMessage.value = message
    reCaptchaStore.executeRecaptcha()
    return
  }

  await submitReport(message)
}

async function submitReport(message: string, recaptchaToken?: string) {
  isLoading.value = true

  try {
    await tapApi.sendComplaint(message, alias.value, recaptchaToken)
    isSuccess.value = true
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped lang="sass">
.report-form
  padding-top: 20px

.title
  color: #000
  text-align: center
  font-size: 20px
  font-weight: 600
  line-height: normal

.sub-title
  color: #363636
  text-align: center
  font-size: 14px
  font-weight: 400
  line-height: normal

:deep(.v-field)
  min-height: 130px

:deep(textarea)
  min-height: 98px
  color: #363636
  font-size: 20px
  line-height: 1.35

:deep(textarea::placeholder)
  color: #B2B7BD
  opacity: 1

:deep(.v-btn)
  min-height: 40px
  height: 40px
  font-size: 16px
  font-weight: 400

@media (max-width: 600px)
  .report-submit
    margin-bottom: 24px

.report-form
  :deep(.v-textarea .v-input__control .v-field__clearable)
    top: -7px
    right: 4px
</style>
